import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {ContextualMenu} from '@Components/contextual-menu';
import {PosterEditorSubtitlesEditor} from '@Components/poster-editor/components/poster-editor-subtitles-editor/poster-editor-subtitles-editor';
import {SubtitleStyleEditingOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-transcript-options/components/subtitle-style-editing-options/subtitle-style-editing-options';
import {useGetSelectedTranscriptItem} from '@Hooks/poster-editor/useGetSelectedTranscriptItem';
import {ItemHeaderTab} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {DefaultEditingOptionsHeader} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-editing-options-header';
import {ItemPositionContextualOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-position-contextual-options';
import {ItemLockInformationBox} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-lock-information-box';
import {PosterItemControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import type {SubtitleTemplateType} from '@PosterWhiteboard/items/transcript-item/subtitle/template-styles.types';
import {getTemplateStylesAndProperties} from '@PosterWhiteboard/items/transcript-item/subtitle/template-styles';
import {PosterEditingTranscriptOptionsSubPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-transcript-options/poster-editing-transcript-options.types';
import {TranscriptCustomizeStylesPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/transcript-customize-styles-panel/transcript-customize-styles-panel';
import {ContextualMenuHeadingOptionWithViewAllButton} from '@Components/contextual-menu/components/contextual-menu-heading-option-with-view-all-button/contextual-menu-heading-option-with-view-all-button';
import {TranscriptTemplatesPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/transcript-templates-panel/transcript-templates-panel';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import styles from './poster-editing-transcript-options.module.scss';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {ContextualMenuContentKeys} from '@Components/poster-editor/components/poster-editing-side-panel/components/contextual-options/contextual-options.types';
import type {ShadowOrGlowOptionsObject} from '@Utils/fabric-shadow.util';
import {convertShadowOptionsObjectToFabricShadowObject} from '@Utils/fabric-shadow.util';

export function PosterEditingTranscriptOptions(): ReactElement {
  const activeItem = useGetSelectedTranscriptItem();
  const [subPanel, setSubPanel] = useState(PosterEditingTranscriptOptionsSubPanel.NONE);
  const [currentItemHeaderTab, setCurrentItemHeaderTab] = useState(ItemHeaderTab.EDIT);
  const [isLoading, setIsLoading] = useState(false);

  if (activeItem === null) {
    throw new Error('Transcript Item is undefined in PosterEditingTranscriptOptions');
  }

  const applyTemplateStyleToAllSubtitles = async (selectedSubtitleTemplateId: string): Promise<void> => {
    setIsLoading(true);
    showLoading('applyTemplateToSubtitles');

    trackPosterBuilderGA4Events(GA4EventName.AI_SUBTITLE_TEMPLATE_PRESET_SELECTED, {[GA4EventParamName.AI_SUBTITLE_TEMPLATE_ID]: selectedSubtitleTemplateId});

    const {shadow, ...otherObj} = getTemplateStylesAndProperties(activeItem.doHideAnimationOptionsAndPresets())[selectedSubtitleTemplateId].templateStyles.stylesToApply;

    await activeItem.updateAllSubtitlesStylesFromSubtitleObject(
      {
        selectedTemplateId: selectedSubtitleTemplateId,
        animationStyle: getTemplateStylesAndProperties(activeItem.doHideAnimationOptionsAndPresets())[selectedSubtitleTemplateId].templateProperties.type,
        ...otherObj,
        shadow: convertShadowOptionsObjectToFabricShadowObject(shadow as ShadowOrGlowOptionsObject, activeItem.getScaleForShadow()),
      },
      {
        undoable: true,
        onError: () => {
          setIsLoading(false);
          hideLoading('applyTemplateToSubtitles');
        },
      },
      true
    );

    setIsLoading(false);
    hideLoading('applyTemplateToSubtitles');
  };

  const applyAnimationStyleToAllSubtitles = async (animationStyle: SubtitleTemplateType): Promise<void> => {
    setIsLoading(true);
    showLoading('applyAnimationStyleToSubtitles');

    await activeItem.updateAllSubtitlesStylesFromSubtitleObject({animationStyle});

    setIsLoading(false);
    hideLoading('applyAnimationStyleToSubtitles');
  };

  const getHeader = (): ReactElement | undefined => {
    if (subPanel === PosterEditingTranscriptOptionsSubPanel.CUSTOMIZE || subPanel === PosterEditingTranscriptOptionsSubPanel.TEMPLATES) {
      return;
    }
    return <DefaultEditingOptionsHeader currentItemHeaderTab={currentItemHeaderTab} onChangeSelectedHeaderItemTab={setCurrentItemHeaderTab} />;
  };

  const getEditOptions = (): ReactElement => {
    return (
      <>
        <ItemLockInformationBox />
        <PosterItemControls />
        <ContextualMenuHeadingOptionWithViewAllButton
          onViewAllClick={() => {
            setSubPanel(PosterEditingTranscriptOptionsSubPanel.TEMPLATES);
          }}
          id="transcript-subtitles-heading-option"
          text={window.i18next.t('pmwjs_presets')}
        />
        <SubtitleStyleEditingOptions
          stylesPreviewImageUrl={activeItem.getPreviewImageUrl()}
          isLoading={isLoading}
          selectedTemplateId={activeItem.getAnySubtitle().selectedTemplateId}
          showScrollIcons
          selectedAnimationStyle={activeItem.getAnySubtitle().animationStyle}
          updateSelectedTemplateId={applyTemplateStyleToAllSubtitles}
          updateAppliedAnimationStyle={applyAnimationStyleToAllSubtitles}
          onCustomizeStylesOptionTriggered={() => {
            setSubPanel(PosterEditingTranscriptOptionsSubPanel.CUSTOMIZE);
          }}
          hideAnimationOptionsAndPresets={activeItem.doHideAnimationOptionsAndPresets()}
        />
        <ContextualMenuHeadingOption id="transcript-subtitles-heading-option" text={window.i18next.t('pmwjs_subtitles')} />
        <PosterEditorSubtitlesEditor />
      </>
    );
  };

  const getPositionOptions = (): ReactElement => {
    return <ItemPositionContextualOptions />;
  };

  const getTemplatesPanel = (): ReactElement => {
    return (
      <TranscriptTemplatesPanel
        isLoading={isLoading}
        selectedTemplateId={activeItem.getAnySubtitle().selectedTemplateId}
        onTemplateSelected={applyTemplateStyleToAllSubtitles}
        onBack={() => {
          setSubPanel(PosterEditingTranscriptOptionsSubPanel.NONE);
        }}
        hideAnimationOptionsAndPresets={activeItem.doHideAnimationOptionsAndPresets()}
      />
    );
  };

  const getCustomizeStylesOptions = (): ReactElement => {
    return (
      <TranscriptCustomizeStylesPanel
        onBack={() => {
          setSubPanel(PosterEditingTranscriptOptionsSubPanel.NONE);
        }}
      />
    );
  };

  const getMainTranscriptEditingOptions = (): ReactElement => {
    switch (currentItemHeaderTab) {
      case ItemHeaderTab.EDIT:
        return getEditOptions();
      case ItemHeaderTab.POSITION:
        return getPositionOptions();
      default:
        throw new Error('unhandled tab item for contextual header');
    }
  };

  const getKey = (): string => {
    return currentItemHeaderTab === ItemHeaderTab.POSITION ? ContextualMenuContentKeys.TRANSCRIPT_DEFAULT_POSITION : ContextualMenuContentKeys.TRANSCRIPT_DEFAULT_EDIT;
  };

  const getContent = (): ReactElement => {
    if (subPanel === PosterEditingTranscriptOptionsSubPanel.CUSTOMIZE) {
      return getCustomizeStylesOptions();
    } else if (subPanel === PosterEditingTranscriptOptionsSubPanel.TEMPLATES) {
      return getTemplatesPanel();
    }
    return (
      <ContextualMenu contentKey={getKey()} id="transcript-contextual-option" contentClassName={styles.transcriptOptionsMenu} header={getHeader()}>
        {getMainTranscriptEditingOptions()}
      </ContextualMenu>
    );
  };

  return getContent();
}
